/**
 * API e stato server dei limiti operativi di trading (SPEC-012 §8).
 *
 * ⚠️ Soglie, consumi e residui sono STRINGHE decimali (ADR-0006), anche quando
 * rappresentano un conteggio: si formattano con `lib/format.ts` e non si
 * convertono in `number` per fare calcoli.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type LimitType =
  | 'operation_count'
  | 'traded_amount'
  | 'single_operation_amount'
  | 'single_operation_portfolio_pct';

export type LimitSide = 'any' | 'buy' | 'sell';
export type LimitWindow = 'operation' | 'day' | 'month';

export type TradingLimit = {
  id: string;
  scope: 'user' | 'portfolio';
  portfolioId: string | null;
  portfolioName: string | null;
  limitType: LimitType;
  side: LimitSide;
  windowUnit: LimitWindow;
  perInstrument: boolean;
  threshold: string;
  enabled: boolean;
  notes: string | null;
  /** Frase già composta dal backend: la UI non ricostruisce gli assi. */
  description: string;
  /** `null` per i limiti sulla singola operazione, che non hanno contatore. */
  used: string | null;
  remaining: string | null;
  windowStartsAt: string | null;
  windowEndsAt: string | null;
  perInstrumentUsage: Array<{ instrumentId: string; name: string; symbol: string | null; used: string }>;
  createdAt: string;
};

export type TradingLimitSettings = {
  enforceManualOrders: boolean;
  /** Fuso su cui chiudono giorno e mese: dichiarato dal backend, non indovinato. */
  timezone: string;
};

export type TradingLimitsResponse = {
  settings: TradingLimitSettings;
  items: TradingLimit[];
  /** Quanti limiti globali esistono, anche quando la vista è filtrata su un portafoglio. */
  globalCount: number;
  total: number;
};

export type CreateLimitInput = {
  portfolioId: string | null;
  limitType: LimitType;
  side: LimitSide;
  windowUnit: LimitWindow;
  perInstrument: boolean;
  threshold: string;
  notes?: string | null;
};

export type SimulateInput = {
  portfolioId: string | null;
  instrumentId?: string | null;
  side: 'buy' | 'sell';
  quantity: string;
  orderType: 'market' | 'limit' | 'stop' | 'stop_limit';
  limitPrice?: string | null;
  stopPrice?: string | null;
};

export type SimulationResult = {
  /** `false` = nessun limite governa l'operazione: non verrebbe consumato nulla. */
  enforced: boolean;
  allowed: boolean;
  violations: Array<{
    limitId: string;
    limitType: LimitType;
    scope: 'user' | 'portfolio';
    threshold: string;
    used: string | null;
    message: string;
  }>;
  checked: Array<{ limitId: string; description: string; scope: string; used: string | null; threshold: string }>;
  estimatedAmount: string | null;
  currency: string;
};

/** `'global'` è un valore esplicito, non l'assenza del filtro: sono due viste diverse. */
export type ScopeFilter = 'all' | 'global' | string;

export const tradingLimitKeys = {
  all: ['trading-limits'] as const,
  scoped: (scope: ScopeFilter) => ['trading-limits', scope] as const,
};

function scopeQuery(scope: ScopeFilter): string {
  if (scope === 'all') return '';
  return `?portfolioId=${encodeURIComponent(scope)}`;
}

export function useTradingLimits(scope: ScopeFilter) {
  return useQuery({
    queryKey: tradingLimitKeys.scoped(scope),
    queryFn: ({ signal }) => api.get<TradingLimitsResponse>(`/trading-limits${scopeQuery(scope)}`, signal),
  });
}

export function useCreateLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLimitInput) => api.post<TradingLimit>('/trading-limits', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: tradingLimitKeys.all }),
  });
}

export function useUpdateLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; threshold?: string; enabled?: boolean; notes?: string | null }) =>
      api.patch<TradingLimit>(`/trading-limits/${id}`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: tradingLimitKeys.all }),
  });
}

export function useDeleteLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/trading-limits/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: tradingLimitKeys.all }),
  });
}

export function useUpdateLimitSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { enforceManualOrders: boolean }) =>
      api.patch<TradingLimitSettings>('/trading-limits/settings', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: tradingLimitKeys.all }),
  });
}

/** Valutazione a secco: non consuma nulla e non invia alcun ordine. */
export function useSimulateOperation() {
  return useMutation({
    mutationFn: (input: SimulateInput) => api.post<SimulationResult>('/trading-limits/simulate', input),
  });
}
